import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { buildBackendUrl } from '../core/config/runtime-config.provider';
import { injectRuntimeConfig } from '../core/config/runtime-config.token';
import { OrderStopRecord } from '../core/state/orders.store';

interface ResultsEnvelope<T> {
  results?: T[];
  data?: T[];
}

interface LegacyMutationEnvelope<T = unknown> {
  error?: boolean;
  error_msg?: string;
  orderId?: string | number;
  result?: T;
  results?: T;
  data?: T;
  Data?: unknown;
}

export interface AvailableTypeRecord {
  typeId: number;
  description: string;
  tariffId: number | null;
  minPrice: number | null;
  source: string | null;
  options: number[];
  fareEstimate: number | null;
  destinationAddress: string;
  destinationLat: number | null;
  destinationLng: number | null;
}

export interface MerchantUserRecord {
  id: string;
  merchantName: string;
  mobile: string | null;
  userPaymentId: number | null;
}

export interface AccountRecord {
  mobile: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}

export interface CreateOrderPayload {
  mobile: string;
  lat: number;
  lng: number;
  address: string;
  comment: string;
  type: number;
  tariffId: number | null;
  payment: number | null;
  options: number[];
  fareEstimate: number | null;
  destinationAddress: string;
  destinationLat: number | null;
  destinationLng: number | null;
}

export interface AttachOrderPayload {
  orderId: string;
  partnerId: string;
  distance?: number | null;
  duration?: number | null;
}

export interface UpdatePreorderStatePayload {
  orderIds: string[];
  status: number;
  ggOrderId: string | null;
}

export interface OrderDirectionResult {
  coordinates: Array<[number, number]>;
  distance: number;
  duration: number;
}

export interface AssistOrderResult {
  assistant: string | null;
  timer: number | null;
}

// A hung request would otherwise stay pending forever, hold one of the browser's 6 connections per host,
// and (for polling) pile up behind the next tick.
// Keep this ABOVE the backend's database statement timeout (DB_STATEMENT_TIMEOUT_MS, 15 s): the database should
// give up first so the request fails with an error, instead of the browser abandoning a query that keeps running.
export const READ_REQUEST_TIMEOUT_MS = 20_000;

@Injectable({ providedIn: 'root' })
export class MonitorBackendApiService {
  private readonly http = inject(HttpClient);
  private readonly runtimeConfig = injectRuntimeConfig();

  getOrders(): Promise<unknown[]> {
    return this.getResults('orders');
  }

  getCancelledOrders(minutes: number): Promise<unknown[]> {
    return this.getResults('orders/cancelled', { minutes: `${minutes}` });
  }

  getPartners(): Promise<unknown[]> {
    return this.getResults('partners');
  }

  // Split form of getPartners(): slow-changing details (fetched rarely) and fast-changing live rows.
  getPartnerDetails(): Promise<unknown[]> {
    return this.getResults('partners/details');
  }

  getPartnerLive(): Promise<unknown[]> {
    return this.getResults('partners/live');
  }

  getOnlineUsers(): Promise<unknown[]> {
    return this.getResults('general/onlineusers');
  }

  getRegions(): Promise<unknown[]> {
    return this.getResults('general/regions');
  }

  getTariffs(): Promise<unknown[]> {
    return this.getResults('general/tariffs');
  }

  getOptions(): Promise<unknown[]> {
    return this.getResults('general/options');
  }

  async getMyAccount(): Promise<AccountRecord> {
    const response = await firstValueFrom(
      this.http.get<LegacyMutationEnvelope<Record<string, unknown>>>(
        buildBackendUrl(this.runtimeConfig, 'general/account')
      )
    );

    throwIfLegacyMutationFailed(response, 'Cannot Load Account');

    const record = asRecord(response.data);

    return {
      mobile: readString(record, 'mobile'),
      firstName: readString(record, 'firstName'),
      lastName: readString(record, 'lastName'),
      email: readString(record, 'email')
    };
  }

  async getMerchantUsers(): Promise<MerchantUserRecord[]> {
    return (await this.getResults('general/merchants/users'))
      .map((entry) => normalizeMerchantUser(entry))
      .filter((entry): entry is MerchantUserRecord => !!entry);
  }

  async getAvailableTypes(
    latitude: number,
    longitude: number,
    time?: string | null
  ): Promise<AvailableTypeRecord[]> {
    const response = await firstValueFrom(
      this.http.get<ResultsEnvelope<unknown>>(buildBackendUrl(this.runtimeConfig, 'general/types'), {
        params: {
          lat: `${latitude}`,
          lng: `${longitude}`,
          ...(time ? { time } : {})
        }
      })
    );

    const types = (response.results ?? response.data ?? [])
      .map((entry) => normalizeAvailableType(entry))
      .filter((entry): entry is AvailableTypeRecord => !!entry);

    if (!types.some((entry) => entry.typeId === 0)) {
      types.push({
        typeId: 0,
        description: 'All',
        tariffId: null,
        minPrice: null,
        source: null,
        options: [],
        fareEstimate: null,
        destinationAddress: '',
        destinationLat: null,
        destinationLng: null
      });
    }

    return types;
  }

  getPartnerCounts(regionIds: number[], carTypeIds: number[]): Promise<unknown[]> {
    return this.getResults('partners/count', {
      regions: JSON.stringify(regionIds),
      cartypes: JSON.stringify(carTypeIds)
    });
  }

  getPartnersWithNegativeBalance(): Promise<unknown[]> {
    return this.getResults('partners/negativebalance');
  }

  getPartnerLocations(): Promise<unknown[]> {
    return this.getResults('partners/locations');
  }

  getOrderHeatmap(tariffId: number): Promise<unknown> {
    return firstValueFrom(
      this.http.get<unknown>(buildBackendUrl(this.runtimeConfig, 'orders/heatmap'), {
        params: {
          tariffId: `${tariffId}`
        }
      })
    );
  }

  async createOrder(payload: CreateOrderPayload): Promise<{ orderId: string | null }> {
    const response = await firstValueFrom(
      this.http.post<LegacyMutationEnvelope>(buildBackendUrl(this.runtimeConfig, 'orders'), payload)
    );

    throwIfLegacyMutationFailed(response, 'Cannot Create Order');

    return {
      orderId: response.orderId !== undefined && response.orderId !== null ? `${response.orderId}` : null
    };
  }

  async attachOrder(payload: AttachOrderPayload): Promise<void> {
    const response = await firstValueFrom(
      this.http.put<LegacyMutationEnvelope>(
        buildBackendUrl(this.runtimeConfig, 'orders/attach'),
        payload
      )
    );

    throwIfLegacyMutationFailed(response, 'Cannot Attach Driver');
  }

  async cancelOrder(orderId: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.delete<LegacyMutationEnvelope>(buildBackendUrl(this.runtimeConfig, 'orders'), {
        params: {
          id: orderId
        }
      })
    );

    throwIfLegacyMutationFailed(response, 'Cannot Cancel Order');
  }

  async updatePreOrderState(payload: UpdatePreorderStatePayload): Promise<unknown> {
    const response = await firstValueFrom(
      this.http.put<LegacyMutationEnvelope>(
        buildBackendUrl(this.runtimeConfig, 'orders/preorders'),
        {
          orderids: payload.orderIds,
          status: payload.status,
          ggorderid: payload.ggOrderId
        }
      )
    );

    throwIfLegacyMutationFailed(response, 'Cannot Create Order');
    return response.result ?? null;
  }

  async addPreorderToOrder(orderId: string, merchantOrderId: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.put<LegacyMutationEnvelope>(
        buildBackendUrl(this.runtimeConfig, 'orders/addpreorder'),
        {
          orderId,
          merchantOrderId
        }
      )
    );

    throwIfLegacyMutationFailed(response, 'Cannot Add PreOrder To An Order');
  }

  async cancelPreorders(merchantOrderIds: string[]): Promise<void> {
    const response = await firstValueFrom(
      this.http.put<LegacyMutationEnvelope>(
        buildBackendUrl(this.runtimeConfig, 'orders/cancelpreorders'),
        {
          merchantOrderIds
        }
      )
    );

    throwIfLegacyMutationFailed(response, 'Cannot Cancel Order');
  }

  async assistOrder(orderId: string): Promise<AssistOrderResult> {
    const response = await firstValueFrom(
      this.http.post<LegacyMutationEnvelope<{ assistant?: unknown; timer?: unknown }>>(
        buildBackendUrl(this.runtimeConfig, 'orders/assist'),
        {
          orderId
        }
      )
    );

    throwIfLegacyMutationFailed(response, 'Cannot get assistance status');

    const payload = response.results ?? response.result ?? response.data ?? response.Data ?? {};

    return {
      assistant: readIdentifier(asRecord(payload), 'assistant'),
      timer: readNumber(asRecord(payload), 'timer')
    };
  }

  async getOrderDirection(
    startLng: number,
    startLat: number,
    endLng: number,
    endLat: number,
    waypoints: Array<{ lat: number; lng: number }> = []
  ): Promise<OrderDirectionResult> {
    const response = await firstValueFrom(
      this.http.get<LegacyMutationEnvelope<{ polylines?: { coordinates?: unknown[] }; distance?: unknown; duration?: unknown }>>(
        buildBackendUrl(this.runtimeConfig, 'orders/direction'),
        {
          params: {
            startLng: `${startLng}`,
            startLat: `${startLat}`,
            endLng: `${endLng}`,
            endLat: `${endLat}`,
            ...(waypoints.length > 0 ? { waypoints: JSON.stringify(waypoints) } : {})
          }
        }
      )
    );

    throwIfLegacyMutationFailed(response, 'Cannot get direction');

    const data = asRecord(response.Data);
    const polylineRecord = asRecord(data['polylines']);

    return {
      coordinates: readArray(polylineRecord, 'coordinates')
        .map((coordinate) => {
          if (!Array.isArray(coordinate) || coordinate.length < 2) {
            return null;
          }

          return [Number(coordinate[1]), Number(coordinate[0])] as [number, number];
        })
        .filter((coordinate): coordinate is [number, number] => !!coordinate),
      distance: readNumber(data, 'distance') ?? 0,
      duration: readNumber(data, 'duration') ?? 0
    };
  }

  async saveOrderStops(orderId: string, stops: OrderStopRecord[]): Promise<void> {
    const response = await firstValueFrom(
      this.http.post<LegacyMutationEnvelope>(buildBackendUrl(this.runtimeConfig, 'orders/stops'), {
        orderId,
        stops
      })
    );

    throwIfLegacyMutationFailed(response, 'Cannot Update Order');
  }

  private async getResults(
    endpoint: string,
    params?: Record<string, string>
  ): Promise<unknown[]> {
    const response = await firstValueFrom(
      this.http
        .get<ResultsEnvelope<unknown>>(buildBackendUrl(this.runtimeConfig, endpoint), { params })
        .pipe(timeout(READ_REQUEST_TIMEOUT_MS))
    );

    return response.results ?? response.data ?? [];
  }
}

function normalizeAvailableType(rawValue: unknown): AvailableTypeRecord | null {
  const record = asRecord(rawValue);
  const typeId = readNumber(record, 'typeId');

  if (typeId === null) {
    return null;
  }

  return {
    typeId,
    description: readString(record, 'description') ?? `Type ${typeId}`,
    tariffId: readNumber(record, 'tariffId'),
    minPrice: readNumber(record, 'minPrice'),
    source: readString(record, 'source'),
    options: readArray(record, 'options')
      .map((entry) => {
        if (typeof entry === 'number') {
          return entry;
        }

        return readNumber(asRecord(entry), 'optionId') ?? readNumber(asRecord(entry), 'id');
      })
      .filter((entry): entry is number => entry !== null),
    fareEstimate: readNumber(record, 'fareEstimate'),
    destinationAddress: readString(record, 'destinationAddress') ?? '',
    destinationLat: readNumber(record, 'destinationLat'),
    destinationLng: readNumber(record, 'destinationLng')
  };
}

function normalizeMerchantUser(rawValue: unknown): MerchantUserRecord | null {
  const record = asRecord(rawValue);
  const merchantName = readString(record, 'merchantName');

  if (!merchantName) {
    return null;
  }

  return {
    id:
      readIdentifier(record, 'userId') ??
      readIdentifier(record, 'merchantId') ??
      readIdentifier(record, 'id') ??
      `${merchantName}:${readString(record, 'mobile') ?? ''}`,
    merchantName,
    mobile: readString(record, 'mobile'),
    userPaymentId: readNumber(record, 'userPaymentId')
  };
}

function throwIfLegacyMutationFailed(response: LegacyMutationEnvelope, fallbackMessage: string): void {
  if (response.error === false || response.error === undefined) {
    return;
  }

  throw new Error(response.error_msg || fallbackMessage);
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function readArray(record: Record<string, unknown>, key: string): unknown[] {
  const value = record[key];
  return Array.isArray(value) ? value : [];
}

function readNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readIdentifier(record: Record<string, unknown>, key: string): string | null {
  const stringValue = readString(record, key);

  if (stringValue) {
    return stringValue;
  }

  const numericValue = readNumber(record, key);
  return numericValue !== null ? `${numericValue}` : null;
}
